/* ======================================
   Maison d'Etto v2 — global behaviour
   - scroll reveal for .v2-reveal
   - toast utility (window.V2Toast)
   - accord/meter bar fill on entry ([data-v2-fill])
   ====================================== */
(function () {
  'use strict';

  /* ---------- toast ---------- */
  var toastTimer = null;
  function toast(message, duration) {
    var el = document.querySelector('[data-v2-toast]');
    if (!el) {
      el = document.createElement('div');
      el.className = 'v2-toast';
      el.setAttribute('role', 'status');
      el.setAttribute('aria-live', 'polite');
      el.setAttribute('data-v2-toast', '');
      document.body.appendChild(el);
    }
    el.textContent = message;
    el.classList.add('is-on');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(function () { el.classList.remove('is-on'); }, duration || 2600);
  }
  window.V2Toast = toast;

  /* ---------- scroll reveal ---------- */
  var revealObserver = null;
  function observeReveals(scope) {
    var nodes = (scope || document).querySelectorAll('.v2-reveal:not(.is-in)');
    if (!nodes.length) return;
    if (!('IntersectionObserver' in window) || document.documentElement.classList.contains('v2-in-editor')) {
      nodes.forEach(function (n) { n.classList.add('is-in'); });
      return;
    }
    if (!revealObserver) {
      revealObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-in');
            revealObserver.unobserve(entry.target);
          }
        });
      }, { threshold: 0.12 });
    }
    nodes.forEach(function (n) { revealObserver.observe(n); });
  }
  window.V2Reveal = observeReveals;

  /* ---------- bar fills ([data-v2-fill="72"] → width 72%) ---------- */
  var fillObserver = null;
  function observeFills(scope) {
    var nodes = (scope || document).querySelectorAll('[data-v2-fill]:not(.is-filled)');
    if (!nodes.length) return;
    function fill(node) {
      node.style.width = node.getAttribute('data-v2-fill') + '%';
      node.classList.add('is-filled');
    }
    if (!('IntersectionObserver' in window)) { nodes.forEach(fill); return; }
    if (!fillObserver) {
      fillObserver = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) { fill(entry.target); fillObserver.unobserve(entry.target); }
        });
      }, { threshold: 0.3 });
    }
    nodes.forEach(function (n) { fillObserver.observe(n); });
    /* safety net: never leave the data unrendered */
    setTimeout(function () { nodes.forEach(function (n) { if (!n.classList.contains('is-filled')) fill(n); }); }, 1800);
  }
  window.V2Fills = observeFills;

  /* ------------------------------------------------------------------
     accessibility widget (accessiBe) vs. overlays
     The widget renders its trigger inside the shadow DOM of
     <access-widget-ui>, and its own !important rules win over anything
     the document stylesheet says about the host. So while the bag or the
     mobile menu is open we flag each host and inject a rule *inside* its
     shadow root, which is the only place that can outrank the widget.
  ------------------------------------------------------------------ */
  var OVERLAY_CLASSES = ['v2-cart-open', 'v2-menu-open', 'v2-filters-open'];
  var A11Y_HOST = 'access-widget-ui';
  var A11Y_FLAG = 'data-v2-overlay';

  function overlayOpen() {
    var html = document.documentElement;
    return OVERLAY_CLASSES.some(function (c) { return html.classList.contains(c); });
  }

  function prepareA11yHost(host) {
    var root = host.shadowRoot;
    if (!root || root.querySelector('style[data-v2-overlay-style]')) return;
    var style = document.createElement('style');
    style.setAttribute('data-v2-overlay-style', '');
    style.textContent = ':host([' + A11Y_FLAG + ']) [data-testid="acsb-trigger"], :host([' + A11Y_FLAG + ']) .trigger-button { display: none !important; visibility: hidden !important; }';
    root.appendChild(style);
  }

  function syncA11yWidget() {
    var hide = overlayOpen();
    var hosts = document.querySelectorAll(A11Y_HOST);
    for (var i = 0; i < hosts.length; i++) {
      prepareA11yHost(hosts[i]);
      if (hide) hosts[i].setAttribute(A11Y_FLAG, '');
      else hosts[i].removeAttribute(A11Y_FLAG);
    }
  }

  function watchA11yWidget() {
    if (!('MutationObserver' in window)) return;
    new MutationObserver(syncA11yWidget).observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    /* the widget loads asynchronously; catch hosts that appear later */
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        for (var j = 0; j < records[i].addedNodes.length; j++) {
          var n = records[i].addedNodes[j];
          if (n.nodeType === 1 && n.tagName && n.tagName.toLowerCase() === A11Y_HOST) { syncA11yWidget(); return; }
        }
      }
    }).observe(document.body, { childList: true });
    syncA11yWidget();
  }
  window.V2A11yWidgetSync = syncA11yWidget;

  /* ------------------------------------------------------------------
     mobile carousel progress: a hairline whose filled segment mirrors
     the visible window of a [data-v2-carousel] scroll track
  ------------------------------------------------------------------ */
  function initCarousels(root) {
    var tracks = root.querySelectorAll('[data-v2-carousel]:not([data-v2-carousel-ready])');
    for (var i = 0; i < tracks.length; i++) bindCarousel(tracks[i]);
  }

  function bindCarousel(track) {
    track.setAttribute('data-v2-carousel-ready', '');
    var bar = track.parentElement && track.parentElement.querySelector('[data-v2-carousel-progress] span');
    if (!bar) return;
    var frame = null;
    function paint() {
      frame = null;
      var total = track.scrollWidth, view = track.clientWidth;
      if (total <= view + 1) { bar.style.width = '100%'; bar.style.transform = 'translateX(0)'; return; }
      var ratio = view / total;
      bar.style.width = (ratio * 100) + '%';
      var maxShift = 1 - ratio;
      var progress = track.scrollLeft / (total - view);
      bar.style.transform = 'translateX(' + ((progress * maxShift / ratio) * 100) + '%)';
    }
    function schedule() { if (frame === null) frame = requestAnimationFrame(paint); }
    track.addEventListener('scroll', schedule, { passive: true });
    window.addEventListener('resize', schedule);
    if ('ResizeObserver' in window) new ResizeObserver(schedule).observe(track);
    paint();
  }
  window.V2Carousels = initCarousels;

  function boot() {
    observeReveals(document);
    observeFills(document);
    watchA11yWidget();
    initCarousels(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  /* theme editor: re-observe when a section is (re)loaded */
  document.addEventListener('shopify:section:load', function (e) {
    observeReveals(e.target);
    observeFills(e.target);
    initCarousels(e.target);
  });
})();
